// src/components/Booklet/BookletCreator.js
import React, { useState } from 'react';
import { Upload, BookOpen, Settings, Palette, Eye, ArrowRight } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookletService } from '../../services/bookletService';
import toast from 'react-hot-toast';

const BookletCreator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [bookletData, setBookletData] = useState({
    title: '',
    description: '',
    pageTransition: 'flip',
    theme: 'modern',
    settings: {
      showNavigation: true,
      showPageNumbers: true,
      allowDownload: false,
      backgroundColor: '#ffffff'
    }
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        setBookletData(prev => ({
          ...prev,
          title: acceptedFiles[0].name.replace('.pdf', '')
        }));
        setStep(2);
      }
    }
  });

  const transitions = [
    { id: 'flip', name: 'Page Flip', description: 'Classic book-like page flipping' },
    { id: 'slide', name: 'Slide', description: 'Smooth horizontal sliding' },
    { id: 'fade', name: 'Fade', description: 'Gentle fade transition' },
    { id: 'curl', name: 'Page Curl', description: 'Realistic page curling effect' },
    { id: 'fold', name: 'Accordion Fold', description: 'Accordion-style folding' }
  ];

  const themes = [
    { id: 'modern', name: 'Modern', preview: 'bg-gradient-to-br from-blue-500 to-purple-600' },
    { id: 'classic', name: 'Classic', preview: 'bg-gradient-to-br from-amber-600 to-orange-700' },
    { id: 'minimal', name: 'Minimal', preview: 'bg-gradient-to-br from-gray-600 to-gray-800' },
    { id: 'vibrant', name: 'Vibrant', preview: 'bg-gradient-to-br from-pink-500 to-violet-600' },
    { id: 'nature', name: 'Nature', preview: 'bg-gradient-to-br from-green-600 to-teal-600' }
  ];

  const createBooklet = async () => {
    if (!pdfFile || !bookletData.title.trim()) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const booklet = await bookletService.createBookletFromPDF(pdfFile, bookletData, user.uid);
      toast.success('Booklet created successfully!');
      navigate(`/booklet/${booklet.id}/edit`);
    } catch (error) {
      toast.error('Error creating booklet: ' + error.message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBookletData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSettingChange = (setting, value) => {
    setBookletData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [setting]: value
      }
    }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <BookOpen className="w-16 h-16 mx-auto text-indigo-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Interactive Booklet</h1>
        <p className="text-gray-600">Transform your PDF into an engaging, interactive digital booklet</p>
      </div>

      {/* Progress Steps */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="text-sm font-medium">Upload PDF</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Configure</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="text-sm font-medium">Create</span>
          </div>
        </div>
      </div>

      {/* Step 1: Upload PDF */}
      {step === 1 && (
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors
              ${isDragActive 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-gray-300 hover:border-gray-400'
              }
            `}
          >
            <input {...getInputProps()} />
            <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-xl font-medium text-gray-600 mb-2">
              {isDragActive ? 'Drop your PDF here...' : 'Upload PDF Document'}
            </p>
            <p className="text-gray-500 mb-4">
              Drag and drop your PDF file here, or click to browse
            </p>
            <div className="text-sm text-gray-400">
              Supported format: PDF • Max size: 50MB
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Configure Booklet */}
      {step === 2 && pdfFile && (
        <div className="bg-white rounded-lg shadow-sm border p-8 space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booklet Title *
                </label>
                <input
                  type="text"
                  value={bookletData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter booklet title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF File
                </label>
                <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                  <Upload className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700 truncate">{pdfFile.name}</span>
                  <button
                    onClick={() => {
                      setPdfFile(null);
                      setStep(1);
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={bookletData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={3}
                placeholder="Enter a description for your booklet"
              />
            </div>
          </div>

          {/* Page Transition */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Page Transition Style</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {transitions.map((transition) => (
                <div
                  key={transition.id}
                  onClick={() => handleInputChange('pageTransition', transition.id)}
                  className={`
                    p-4 border-2 rounded-lg cursor-pointer transition-colors
                    ${bookletData.pageTransition === transition.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <h4 className="font-medium text-gray-900">{transition.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{transition.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Theme Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {themes.map((theme) => (
                <div
                  key={theme.id}
                  onClick={() => handleInputChange('theme', theme.id)}
                  className={`
                    relative p-4 border-2 rounded-lg cursor-pointer transition-colors
                    ${bookletData.theme === theme.id
                      ? 'border-indigo-500'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className={`w-full h-16 rounded-lg mb-3 ${theme.preview}`}></div>
                  <p className="text-sm font-medium text-center">{theme.name}</p>
                  {bookletData.theme === theme.id && (
                    <div className="absolute top-2 right-2 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Display Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Show Navigation</label>
                  <p className="text-xs text-gray-500">Display navigation controls</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bookletData.settings.showNavigation}
                    onChange={(e) => handleSettingChange('showNavigation', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Show Page Numbers</label>
                  <p className="text-xs text-gray-500">Display current page number</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bookletData.settings.showPageNumbers}
                    onChange={(e) => handleSettingChange('showPageNumbers', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Allow Download</label>
                  <p className="text-xs text-gray-500">Let viewers download the original PDF</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bookletData.settings.allowDownload}
                    onChange={(e) => handleSettingChange('allowDownload', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={createBooklet}
              disabled={isLoading || !bookletData.title.trim()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Create Booklet
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookletCreator;